'use client';

import { useState, useEffect } from 'react';
import { BrainCircuitIcon } from '@/components/shared/AIComponents';

interface DemoConversation {
  query: string;
  response: string;
  table?: {
    headers: string[];
    rows: string[][];
  };
  actions?: string[];
}

const demoConversations: DemoConversation[] = [
  {
    query: 'Which clients have overdue notices?',
    response: 'You have 5 overdue notices across 3 clients:',
    table: {
      headers: ['Client', 'Section', 'Due Date', 'Status'],
      rows: [
        ['ABC Corp', '143(1)', 'Dec 20', 'Overdue'],
        ['XYZ Ltd', '142(1)', 'Dec 18', 'Overdue'],
        ['ABC Corp', '148', 'Dec 22', 'Urgent'],
        ['PQR Pvt', '154', 'Dec 19', 'Overdue'],
        ['XYZ Ltd', '143(2)', 'Dec 21', 'Pending'],
      ],
    },
    actions: ['View All Notices', 'Send Reminders'],
  },
  {
    query: 'Show me high-value demands above 5 lakhs',
    response: 'Found 4 high-value demands totaling Rs. 42.5L:',
    table: {
      headers: ['Client', 'Amount', 'AY', 'Status'],
      rows: [
        ['ABC Corp', '₹15.2L', '2023-24', 'Pending'],
        ['PQR Pvt', '₹12.8L', '2022-23', 'Disputed'],
        ['XYZ Ltd', '₹8.5L', '2023-24', 'Under Review'],
        ['LMN Inc', '₹6.0L', '2021-22', 'Payment Due'],
      ],
    },
    actions: ['Export Report', 'View Details'],
  },
  {
    query: "What's my compliance status for this week?",
    response: "This week's compliance overview:",
    table: {
      headers: ['Task', 'Count', 'Priority'],
      rows: [
        ['Notices Due', '3', 'High'],
        ['Responses Pending', '7', 'Medium'],
        ['Demands to Review', '2', 'High'],
        ['Returns to File', '5', 'Normal'],
      ],
    },
    actions: ['View Calendar', 'Generate Report'],
  },
];

export default function FinkAIDemoSection() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [phase, setPhase] = useState<'typing' | 'thinking' | 'responding'>('typing');
  const [displayedQuery, setDisplayedQuery] = useState('');
  const [showResponse, setShowResponse] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [visibleRows, setVisibleRows] = useState(0);

  const currentConvo = demoConversations[currentIndex];

  useEffect(() => {
    // Reset for new conversation
    setPhase('typing');
    setDisplayedQuery('');
    setShowResponse(false);
    setShowTable(false);
    setShowActions(false);
    setVisibleRows(0);

    // Typing animation
    let charIndex = 0;
    const typingInterval = setInterval(() => {
      if (charIndex <= currentConvo.query.length) {
        setDisplayedQuery(currentConvo.query.slice(0, charIndex));
        charIndex++;
      } else {
        clearInterval(typingInterval);
        setPhase('thinking');

        // Thinking phase
        setTimeout(() => {
          setPhase('responding');
          setShowResponse(true);

          // Show table rows one by one
          if (currentConvo.table) {
            setTimeout(() => {
              setShowTable(true);
              let rowIndex = 0;
              const rowInterval = setInterval(() => {
                if (rowIndex <= currentConvo.table!.rows.length) {
                  setVisibleRows(rowIndex);
                  rowIndex++;
                } else {
                  clearInterval(rowInterval);
                  // Show actions
                  setTimeout(() => {
                    setShowActions(true);

                    // Move to next conversation after pause
                    setTimeout(() => {
                      setCurrentIndex((prev) => (prev + 1) % demoConversations.length);
                    }, 3000);
                  }, 500);
                }
              }, 200);
            }, 500);
          }
        }, 1500);
      }
    }, 50);

    return () => clearInterval(typingInterval);
  }, [currentIndex]);

  return (
    <section id="fink-ai-demo" className="py-12 lg:py-24 bg-gradient-to-br from-gray-50 via-white to-violet-50/30 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-8 lg:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 lg:px-4 lg:py-2 bg-violet-100 border border-violet-200 rounded-full mb-4">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
            </span>
            <span className="text-xs lg:text-sm font-medium text-violet-700">Live Demo</span>
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 lg:mb-4 px-4">
            Meet Fink, Your AI Assistant
          </h2>
          <p className="text-sm lg:text-lg text-gray-600 max-w-2xl mx-auto px-4">
            Built into Finkr, Fink instantly answers complex queries about your tax data
          </p>
        </div>

        {/* Demo Window */}
        <div className="max-w-3xl mx-auto">
          <div className="rounded-2xl overflow-hidden shadow-xl border border-gray-200 bg-white">
            {/* Window Header */}
            <div className="bg-gradient-to-r from-violet-600 via-indigo-600 to-cyan-600 px-4 py-3 lg:px-6 lg:py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 lg:gap-3">
                  <div className="w-8 h-8 lg:w-10 lg:h-10 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
                    <BrainCircuitIcon className="w-5 h-5 lg:w-6 lg:h-6 text-white" animate />
                  </div>
                  <div>
                    <h3 className="text-sm lg:text-base font-semibold text-white">Fink</h3>
                    <p className="text-[10px] lg:text-xs text-white/70 flex items-center gap-1">
                      <span className="w-1.5 h-1.5 bg-green-400 rounded-full"></span>
                      AI Assistant in Finkr
                    </p>
                  </div>
                </div>
                <div className="hidden sm:flex gap-2">
                  <div className="w-3 h-3 rounded-full bg-white/20"></div>
                  <div className="w-3 h-3 rounded-full bg-white/20"></div>
                  <div className="w-3 h-3 rounded-full bg-white/20"></div>
                </div>
              </div>
            </div>

            {/* Chat Content - Light Theme */}
            <div className="bg-gradient-to-b from-gray-50 to-white p-4 lg:p-6 min-h-[320px] lg:min-h-[400px]">
              {/* User Query */}
              <div className="flex justify-end mb-3 lg:mb-4">
                <div className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white px-3 py-2 lg:px-4 lg:py-3 rounded-2xl rounded-br-md max-w-[85%] shadow-sm">
                  <p className="text-xs lg:text-sm">{displayedQuery}<span className={phase === 'typing' ? 'animate-pulse' : 'hidden'}>|</span></p>
                </div>
              </div>

              {/* AI Response */}
              {phase !== 'typing' && (
                <div className="flex gap-2 lg:gap-3">
                  <div className="w-6 h-6 lg:w-8 lg:h-8 rounded-lg bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center flex-shrink-0 shadow-sm">
                    <BrainCircuitIcon className="w-4 h-4 lg:w-5 lg:h-5 text-white" animate={phase === 'thinking'} />
                  </div>
                  <div className="flex-1 min-w-0">
                    {phase === 'thinking' && (
                      <div className="bg-white rounded-2xl rounded-bl-md px-3 py-2 lg:px-4 lg:py-3 inline-block border border-gray-100 shadow-sm">
                        <div className="flex items-center gap-2">
                          <span className="text-xs lg:text-sm text-gray-600">Fink is analyzing</span>
                          <span className="flex gap-1">
                            <span className="w-1.5 h-1.5 bg-violet-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                            <span className="w-1.5 h-1.5 bg-violet-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                            <span className="w-1.5 h-1.5 bg-violet-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                          </span>
                        </div>
                      </div>
                    )}

                    {showResponse && (
                      <div className="space-y-3 lg:space-y-4">
                        <div className={`bg-white rounded-2xl rounded-bl-md px-3 py-2 lg:px-4 lg:py-3 border border-gray-100 shadow-sm transition-all duration-300 ${showResponse ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
                          <p className="text-xs lg:text-sm text-gray-800">{currentConvo.response}</p>
                        </div>

                        {/* Table - Light Theme with horizontal scroll on mobile */}
                        {showTable && currentConvo.table && (
                          <div className="bg-white rounded-xl overflow-hidden border border-gray-200 shadow-sm transition-all duration-300">
                            <div className="overflow-x-auto scrollbar-hide">
                              <table className="w-full text-xs lg:text-sm min-w-[400px]">
                                <thead>
                                  <tr className="bg-gradient-to-r from-violet-50 to-cyan-50">
                                    {currentConvo.table.headers.map((header, i) => (
                                      <th key={i} className="px-2 py-2 lg:px-4 lg:py-2.5 text-left text-gray-700 font-semibold whitespace-nowrap">
                                        {header}
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {currentConvo.table.rows.slice(0, visibleRows).map((row, i) => (
                                    <tr key={i} className="border-t border-gray-100 hover:bg-gray-50 transition-all duration-200">
                                      {row.map((cell, j) => (
                                        <td key={j} className={`px-2 py-2 lg:px-4 lg:py-2.5 whitespace-nowrap ${j === row.length - 1 ? (cell === 'Overdue' || cell === 'Urgent' || cell === 'High' ? 'text-red-600 font-medium' : cell === 'Pending' || cell === 'Medium' ? 'text-amber-600 font-medium' : 'text-gray-700') : 'text-gray-700'}`}>
                                          {cell}
                                        </td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}

                        {/* Actions - Light Theme */}
                        {showActions && currentConvo.actions && (
                          <div className="flex flex-wrap gap-2">
                            {currentConvo.actions.map((action, i) => (
                              <button
                                key={i}
                                className="px-3 py-1.5 lg:px-4 lg:py-2 text-xs lg:text-sm bg-gradient-to-r from-violet-50 to-cyan-50 text-violet-700 border border-violet-200 rounded-xl hover:from-violet-100 hover:to-cyan-100 transition-all flex items-center gap-1.5 lg:gap-2 shadow-sm touch-manipulation"
                                style={{ animationDelay: `${i * 100}ms` }}
                              >
                                <span className="text-violet-500">→</span>
                                {action}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Input Area - Light Theme */}
            <div className="bg-white border-t border-gray-100 p-3 lg:p-4">
              <div className="flex gap-2 lg:gap-3 items-center">
                <div className="flex-1 bg-gray-50 rounded-xl px-3 py-2.5 lg:px-4 lg:py-3 border border-gray-200">
                  <span className="text-gray-400 text-xs lg:text-sm">Ask Fink anything about your tax data...</span>
                </div>
                <button className="p-2.5 lg:p-3 bg-gradient-to-r from-violet-600 to-indigo-600 rounded-xl shadow-sm touch-manipulation">
                  <svg className="w-4 h-4 lg:w-5 lg:h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Demo indicator dots */}
          <div className="flex justify-center gap-2 mt-4 lg:mt-6">
            {demoConversations.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentIndex(i)}
                className={`h-2 rounded-full transition-all touch-manipulation ${i === currentIndex ? 'w-8 bg-violet-500' : 'w-2 bg-gray-300 hover:bg-gray-400'}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
